// Reference table of processor display names + dashboard deep-links, plus the
// published fee rates as documentation. Mirrors
// PaymentProcessorFeeDefaults.GetCanonicalRate
// (apps/api/Features/Finance/Entities/PaymentProcessorFeeDefaults.cs).
//
// IMPORTANT: the fee numbers here are NOT used to pre-fill or default the
// onboarding fee fields. Product decision — the treasurer must enter their own
// fees explicitly; we never default or interpret published rates. Onboarding
// reads only `displayName` + `lookupUrl` from this table (non-fee display
// hints); the processor-settings page reads `displayName` as a name fallback.
// The rate/flat numbers remain purely as reference documentation.

import { PaymentProcessorKind } from '@signalvote/shared';

export interface CanonicalProcessorRate {
  /** Decimal (e.g. 0.0394 = 3.94%). */
  feeRate: number;
  /** Per-transaction flat fee in dollars; null when the processor has no flat fee. */
  flatFee: number | null;
  /** When the rate was published — substituted into the "Where to find this" hint. */
  publishedAsOf: string;
  /** Deep-link to the processor's balance/rate dashboard; null when unknown. */
  lookupUrl: string | null;
  /** Treasurer-facing label (overrides the bare PaymentProcessorKind enum name). */
  displayName: string;
}

export const CANONICAL_PROCESSOR_RATES: Record<PaymentProcessorKind, CanonicalProcessorRate> = {
  [PaymentProcessorKind.Numero]: {
    feeRate: 0.036,
    flatFee: null,
    publishedAsOf: 'May 2026',
    lookupUrl: null,
    displayName: 'Numero',
  },
  [PaymentProcessorKind.WinRed]: {
    feeRate: 0.0394,
    flatFee: null,
    publishedAsOf: 'May 2026',
    lookupUrl: 'https://winred.com/c/dashboard/accounting',
    displayName: 'WinRed',
  },
  [PaymentProcessorKind.ActBlue]: {
    feeRate: 0.0395,
    flatFee: null,
    publishedAsOf: 'May 2026',
    lookupUrl: 'https://secure.actblue.com/dashboard/payouts',
    displayName: 'ActBlue',
  },
  [PaymentProcessorKind.Stripe]: {
    feeRate: 0.029,
    flatFee: 0.30,
    publishedAsOf: 'May 2026',
    lookupUrl: 'https://dashboard.stripe.com/balance',
    displayName: 'Stripe',
  },
  [PaymentProcessorKind.StripeConnect]: {
    feeRate: 0.034,
    flatFee: 0.30,
    publishedAsOf: 'May 2026',
    lookupUrl: null,
    displayName: 'Online donations',
  },
  [PaymentProcessorKind.Anedot]: {
    feeRate: 0.039,
    flatFee: null,
    publishedAsOf: 'May 2026',
    lookupUrl: 'https://anedot.com/payouts',
    displayName: 'Anedot',
  },
  [PaymentProcessorKind.ManualP2P]: {
    feeRate: 0,
    flatFee: null,
    publishedAsOf: 'May 2026',
    lookupUrl: null,
    displayName: 'Venmo / Zelle / Cash App',
  },
  [PaymentProcessorKind.Other]: {
    feeRate: 0,
    flatFee: null,
    publishedAsOf: 'May 2026',
    lookupUrl: null,
    displayName: 'Other',
  },
};
